import { useEffect } from "react";
import { KeyValuePair } from "@app/types/Page";

type UseSetAdTargetsProps = {
  /** The initial ad targets from the server side. */
  initialAdTargets?: KeyValuePair<string, string>[];
  /** The ad targets to be set in the client side. */
  adTargets?: KeyValuePair<string, string>[];
};

/**
 * Custom hook to set ad targets for the page on the client side.
 * This hook merges client-side ad targets with initial server-side ad targets
 * and updates the global window.bt_page_data object.
 *
 * @example
 * ```tsx
 * useSetAdTargets({
 *   initialAdTargets: loaderData.adSettings?.adTargets,
 *   adTargets: [
 *     { key: "abVariant", value: removeMoreVariant || VariantType.CONTROL }
 *   ]
 * });
 * ```
 */
export const useSetAdTargets = ({
  initialAdTargets = [],
  adTargets = [],
}: UseSetAdTargetsProps) => {
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.bt_page_data) return;

    // Merge initial and new ad targets, with client-side targets taking precedence
    // Create a map of existing targets for easy lookup
    const existingTargetsMap = new Map<string, string>();

    // Add initial targets first
    initialAdTargets.forEach((target) => {
      if (target?.key && target?.value != null) {
        existingTargetsMap.set(target.key, String(target.value));
      }
    });

    // Override/add with client-side targets
    adTargets.forEach((target) => {
      if (target?.key && target?.value != null) {
        existingTargetsMap.set(target.key, String(target.value));
      }
    });

    // Convert map back to array format
    const mergedTargets = Array.from(existingTargetsMap.entries()).map(
      ([key, value]) => ({ key, value })
    );

    // Update the global ad page data
    window.bt_page_data = {
      ...window.bt_page_data,
      adTargets: mergedTargets,
    };
  }, [adTargets, initialAdTargets]);
};
